import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback, useEffect, useRef } from 'react';
import { Subscreens } from '../../constants/usage';
import { MIN_HERO_SECTION_HEIGHT } from '../constants/dimensions';
import { InformationSection } from './InformationSection';
import { MediaSection } from './MediaSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function HeroSection({
  buttonText,
  buttonUrl,
  heroPosition,
  imageUrl,
  isActive = false,
  listingId,
  onClick,
  resultType,
  secondaryButtonText,
  secondaryButtonUrl,
  slug,
  subtitle,
  title,
  videoId,
  openCopilot = false
}) {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const hasMedia = Boolean(imageUrl || videoId);
  const videoCurrentTimeRef = useRef(0);
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      md: false
    }
  });
  const trackInteraction = useCallback(action => {
    trackMarketplaceEvent(getMarketplaceInteractionEvent({
      action,
      listingId,
      itemIndex: heroPosition,
      resultType,
      slug,
      subscreen: Subscreens.HERO_SECTION,
      videoId,
      videoWatchedDuration: videoCurrentTimeRef.current
    }));
  }, [heroPosition, listingId, resultType, slug, trackMarketplaceEvent, videoId]);
  useEffect(() => {
    if (!videoId || !isActive) return;
    const handleVideoEvent = message => {
      var _message$data$payload;
      if (message.origin !== 'https://play.hubspotvideo.com') return;
      if (!message.data) return;

      // Not all events have a currentTime payload, so we need to update the currentTime ref if it exists
      if (((_message$data$payload = message.data.payload) === null || _message$data$payload === void 0 ? void 0 : _message$data$payload.currentTime) !== undefined) {
        videoCurrentTimeRef.current = message.data.payload.currentTime;
      }
      switch (message.data.type) {
        case 'PLAYER_PLAY':
          trackInteraction('hero video played (not on autoplay)');
          break;
        case 'PLAYER_PAUSE':
        case 'PLAYER_ENDED':
          {
            const action = message.data.type === 'PLAYER_PAUSE' ? 'hero video paused' : 'hero video finished';
            trackInteraction(action);
            break;
          }
        default:
          break;
      }
    };
    window.addEventListener('message', handleVideoEvent);
    return () => window.removeEventListener('message', handleVideoEvent);
  }, [videoId, isActive, trackInteraction]);
  const handleHeroButtonClick = useCallback(() => {
    trackInteraction('hero button clicked');
    onClick === null || onClick === void 0 || onClick();
  }, [trackInteraction, onClick]);
  const isSolutionsPartnerHero = Boolean(secondaryButtonText && secondaryButtonUrl);
  const mobileButtonSize = isMobile && isSolutionsPartnerHero ? 'small' : undefined;
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    "data-test-id": "hero-section",
    minBlockSize: MIN_HERO_SECTION_HEIGHT,
    paddingBlockEnd: isSolutionsPartnerHero ? '800' : undefined,
    children: isMobile ? /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(InformationSection, {
        buttonText: buttonText,
        buttonUrl: buttonUrl,
        isMobile: isMobile,
        isSolutionsPartnerHero: isSolutionsPartnerHero,
        mobileButtonSize: mobileButtonSize,
        onButtonClick: handleHeroButtonClick,
        openCopilot: openCopilot,
        secondaryButtonText: secondaryButtonText,
        secondaryButtonUrl: secondaryButtonUrl,
        subtitle: subtitle,
        title: title
      }), hasMedia && /*#__PURE__*/_jsx(MediaSection, {
        imageUrl: imageUrl,
        isMobile: isMobile,
        title: title,
        videoId: videoId
      })]
    }) : /*#__PURE__*/_jsxs(Grid, {
      blockSize: "100%",
      children: [/*#__PURE__*/_jsx(GridItem, {
        size: hasMedia ? 6 : 12,
        children: /*#__PURE__*/_jsx(InformationSection, {
          buttonText: buttonText,
          buttonUrl: buttonUrl,
          isMobile: isMobile,
          isSolutionsPartnerHero: isSolutionsPartnerHero,
          mobileButtonSize: mobileButtonSize,
          onButtonClick: handleHeroButtonClick,
          openCopilot: openCopilot,
          secondaryButtonText: secondaryButtonText,
          secondaryButtonUrl: secondaryButtonUrl,
          subtitle: subtitle,
          title: title
        })
      }), hasMedia && /*#__PURE__*/_jsx(GridItem, {
        size: 6,
        children: /*#__PURE__*/_jsx(MediaSection, {
          imageUrl: imageUrl,
          isMobile: isMobile,
          title: title,
          videoId: videoId
        })
      })]
    })
  });
}